import React, { useState, useContext } from "react";

import {
  Button,
  CssBaseline,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  Divider,
} from "@material-ui/core";

import {
  LockOutlined,
  MailOutline,
  Visibility,
  VisibilityOff,
  WhatsApp as WhatsAppIcon,
  ArrowForward,
} from "@material-ui/icons";

import { makeStyles } from "@material-ui/core/styles";

import { i18n } from "../../translate/i18n";

import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
  root: {
    minHeight: "100vh",
    width: "100%",
    display: "flex",
  },
  // ============== LEFT PANEL (branding) ==============
  leftPanel: {
    flex: 1.4,
    position: "relative",
    overflow: "hidden",
    background:
      "linear-gradient(135deg, #0b1120 0%, #1e293b 45%, #312e81 100%)",
    color: "#fff",
    padding: theme.spacing(8, 8, 6, 8),
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  // Pattern decorativo no fundo
  bgDots: {
    position: "absolute",
    top: 0,
    right: 0,
    width: "60%",
    height: "100%",
    backgroundImage:
      "radial-gradient(circle, rgba(99,102,241,0.18) 1.5px, transparent 1.5px)",
    backgroundSize: "22px 22px",
    pointerEvents: "none",
    maskImage:
      "linear-gradient(to left, rgba(0,0,0,1) 30%, rgba(0,0,0,0) 100%)",
    WebkitMaskImage:
      "linear-gradient(to left, rgba(0,0,0,1) 30%, rgba(0,0,0,0) 100%)",
  },
  // Glow circle decorativo
  glow: {
    position: "absolute",
    width: 480,
    height: 480,
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(124,58,237,0.35) 0%, rgba(124,58,237,0) 70%)",
    bottom: -160,
    right: -160,
    pointerEvents: "none",
  },
  brandRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    position: "relative",
    zIndex: 1,
  },
  brandMark: {
    width: 44,
    height: 44,
    borderRadius: 10,
    background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontWeight: 700,
    fontSize: 16,
    letterSpacing: 0.5,
    boxShadow: "0 6px 18px rgba(79, 70, 229, 0.45)",
  },
  brandText: {
    fontWeight: 700,
    fontSize: "1.15rem",
    letterSpacing: 0.5,
  },
  leftBody: {
    position: "relative",
    zIndex: 1,
    maxWidth: 560,
  },
  headline: {
    fontSize: "2.6rem",
    fontWeight: 800,
    lineHeight: 1.15,
    letterSpacing: -0.5,
    marginBottom: theme.spacing(3),
  },
  headlineHighlight: {
    background:
      "linear-gradient(90deg, #4ade80 0%, #22d3ee 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
  },
  subheadline: {
    fontSize: "1rem",
    lineHeight: 1.6,
    color: "rgba(226, 232, 240, 0.85)",
    marginBottom: theme.spacing(4),
    maxWidth: 480,
  },
  featureList: {
    listStyle: "none",
    padding: 0,
    margin: 0,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
  },
  featureItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    fontSize: "0.92rem",
    color: "rgba(226, 232, 240, 0.92)",
  },
  featureDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: "linear-gradient(135deg, #4ade80 0%, #22d3ee 100%)",
    flexShrink: 0,
  },
  leftFooter: {
    position: "relative",
    zIndex: 1,
    fontSize: "0.78rem",
    color: "rgba(148, 163, 184, 0.7)",
  },
  // ============== RIGHT PANEL (form) ==============
  rightPanel: {
    flex: 1,
    backgroundColor: theme.palette.type === "dark" ? "#0f172a" : "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(4),
  },
  formCard: {
    width: "100%",
    maxWidth: 380,
    display: "flex",
    flexDirection: "column",
  },
  // Brand mostrado SO em mobile (quando left panel some)
  mobileBrand: {
    display: "none",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      display: "flex",
    },
  },
  formTitle: {
    fontWeight: 700,
    fontSize: "1.55rem",
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
  },
  formSubtitle: {
    fontSize: "0.9rem",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(3.5),
  },
  field: {
    marginBottom: theme.spacing(2),
  },
  submit: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1.4, 2),
    fontWeight: 600,
    textTransform: "none",
    fontSize: "0.95rem",
    borderRadius: 8,
    boxShadow: "none",
    background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
    color: "#fff",
    "&:hover": {
      background: "linear-gradient(135deg, #4338ca 0%, #6d28d9 100%)",
      boxShadow: "0 6px 18px rgba(79, 70, 229, 0.3)",
    },
  },
  divider: {
    margin: theme.spacing(3, 0),
  },
  footer: {
    marginTop: theme.spacing(4),
    textAlign: "center",
    color: theme.palette.text.secondary,
    fontSize: "0.75rem",
  },
  footerLink: {
    color: theme.palette.text.secondary,
    textDecoration: "none",
    margin: theme.spacing(0, 1),
    "&:hover": {
      color: theme.palette.primary.main,
    },
  },
}));

const Login = () => {
  const classes = useStyles();

  const [user, setUser] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);

  const { handleLogin } = useContext(AuthContext);

  const handleChangeInput = e => {
    setUser({ ...user, [e.target.name]: e.target.value });
  };

  const handleSubmit = e => {
    e.preventDefault();
    handleLogin(user);
  };

  return (
    <div className={classes.root}>
      <CssBaseline />

      {/* ============== LEFT (branding) ============== */}
      <div className={classes.leftPanel}>
        <div className={classes.bgDots} />
        <div className={classes.glow} />

        <div className={classes.brandRow}>
          <div className={classes.brandMark}>IM</div>
          <Typography className={classes.brandText}>InManager</Typography>
        </div>

        <div className={classes.leftBody}>
          <Typography component="h1" className={classes.headline}>
            {i18n.t("login.brand.headlinePrefix")}{" "}
            <span className={classes.headlineHighlight}>
              {i18n.t("login.brand.headlineHighlight")}
            </span>{" "}
            {i18n.t("login.brand.headlineSuffix")}
          </Typography>
          <Typography className={classes.subheadline}>
            {i18n.t("login.brand.subheadline")}
          </Typography>

          <ul className={classes.featureList}>
            <li className={classes.featureItem}>
              <span className={classes.featureDot} />
              {i18n.t("login.brand.features.0")}
            </li>
            <li className={classes.featureItem}>
              <span className={classes.featureDot} />
              {i18n.t("login.brand.features.1")}
            </li>
            <li className={classes.featureItem}>
              <span className={classes.featureDot} />
              {i18n.t("login.brand.features.2")}
            </li>
          </ul>
        </div>

        <Typography className={classes.leftFooter}>
          © {new Date().getFullYear()} InManager · Todos os direitos reservados
        </Typography>
      </div>

      {/* ============== RIGHT (form) ============== */}
      <div className={classes.rightPanel}>
        <div className={classes.formCard}>
          <div className={classes.mobileBrand}>
            <div className={classes.brandMark}>IM</div>
            <Typography className={classes.brandText}>InManager</Typography>
          </div>

          <Typography className={classes.formTitle}>
            {i18n.t("login.welcomeTitle")}
          </Typography>
          <Typography className={classes.formSubtitle}>
            {i18n.t("login.welcomeSubtitle")}
          </Typography>

          <form noValidate onSubmit={handleSubmit}>
            <TextField
              variant="outlined"
              fullWidth
              id="email"
              label={i18n.t("login.form.email")}
              name="email"
              value={user.email}
              onChange={handleChangeInput}
              autoComplete="email"
              autoFocus
              className={classes.field}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MailOutline fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              variant="outlined"
              fullWidth
              name="password"
              label={i18n.t("login.form.password")}
              id="password"
              value={user.password}
              onChange={handleChangeInput}
              autoComplete="current-password"
              type={showPassword ? "text" : "password"}
              className={classes.field}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlined fontSize="small" color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(e => !e)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? (
                        <VisibilityOff fontSize="small" />
                      ) : (
                        <Visibility fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              className={classes.submit}
              size="large"
              endIcon={<ArrowForward />}
            >
              {i18n.t("login.buttons.submit")}
            </Button>
          </form>

          <Divider className={classes.divider} />

          <div className={classes.footer}>
            <a href="#" className={classes.footerLink}>
              {i18n.t("login.footer.help")}
            </a>
            ·
            <a href="#" className={classes.footerLink}>
              {i18n.t("login.footer.terms")}
            </a>
            ·
            <a href="#" className={classes.footerLink}>
              {i18n.t("login.footer.privacy")}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
